// "Link a device" — the CH6d web UI over the CH6c driver. Two roles:
//   • This device HAS the history → show a QR, stream history out (Primary).
//   • This device is NEW → scan the QR, receive history (New/companion).
// The safety-number compare is a hard gate: nothing is sent or trusted until the
// two humans confirm the numbers match. See docs/specs/chat-device-linking.md.

import { type FocusEvent, useCallback, useEffect, useRef, useState } from 'react';

import { loadLinking } from '@brizina/mls-wasm';

import { toB64 } from '../chat/chatApi';
import { historySink, historySource, refreshAfterTransfer, relayFor } from '../chat/linkAdapters';
import { LinkAbortedError, LinkTimeoutError, runCompanion, runPrimary } from '../chat/linking';
import { encodeQr, LINK_VERSION, parseQr } from '../chat/linkWire';
import { linkingApi } from '../chat/linkingApi';
import { BrizinaLoader } from './BrizinaLoader';
import { QrScan } from './QrScan';
import { QrShow } from './QrShow';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

type Phase =
  | 'choose'
  | 'showing' // Primary: QR up, waiting for the other device
  | 'scanning' // New: camera/paste
  | 'connecting' // New: derived, waiting on the other device
  | 'sas'
  | 'transfer'
  | 'done'
  | 'aborted'
  | 'error';

interface Props {
  me: { id: string };
  onClose: () => void;
}

function friendlyError(e: unknown): string {
  if (e instanceof LinkTimeoutError) return 'The other device did not respond in time. Try again.';
  if (e instanceof Error && e.message) return e.message;
  return 'Something went wrong linking the devices.';
}

export function LinkDeviceModal({ me, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('choose');
  const [sas, setSas] = useState('');
  const [qrText, setQrText] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [count, setCount] = useState(0);
  const [error, setError] = useState('');
  const sasResolve = useRef<((ok: boolean) => void) | null>(null);
  const ridRef = useRef<string | null>(null);
  const runningRef = useRef(false);

  useEffect(
    () => () => {
      // Tear down the rendezvous if the modal closes mid-flight.
      if (ridRef.current) void linkingApi.close(ridRef.current).catch(() => undefined);
      sasResolve.current?.(false);
    },
    [],
  );

  const confirmSas = useCallback(
    (s: string) =>
      new Promise<boolean>((resolve) => {
        setSas(s);
        setPhase('sas');
        sasResolve.current = resolve;
      }),
    [],
  );

  const onProgress = useCallback((done: number, total: number) => setProgress({ done, total }), []);

  const onSasAnswer = useCallback((match: boolean) => {
    const resolve = sasResolve.current;
    sasResolve.current = null;
    if (match) {
      setProgress({ done: 0, total: 0 });
      setPhase('transfer');
    }
    resolve?.(match);
  }, []);

  const onSasMatch = useCallback(() => onSasAnswer(true), [onSasAnswer]);
  const onSasNoMatch = useCallback(() => onSasAnswer(false), [onSasAnswer]);
  const goChoose = useCallback(() => setPhase('choose'), []);
  const goScanning = useCallback(() => setPhase('scanning'), []);
  const selectAll = useCallback((e: FocusEvent<HTMLTextAreaElement>) => e.currentTarget.select(), []);

  const startPrimary = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      const { rendezvousId } = await linkingApi.create();
      ridRef.current = rendezvousId;
      const LinkHandshake = await loadLinking();
      const handshake = new LinkHandshake();
      setQrText(encodeQr({ v: LINK_VERSION, rid: rendezvousId, pub: toB64(handshake.publicKey) }));
      setPhase('showing');
      const { sent } = await runPrimary({
        relay: relayFor(rendezvousId, 'p'),
        handshake,
        accountId: me.id,
        rendezvousId,
        version: LINK_VERSION,
        source: historySource(),
        confirmSas,
        onProgress,
      });
      setCount(sent);
      setPhase('done');
    } catch (e) {
      if (e instanceof LinkAbortedError) setPhase('aborted');
      else {
        setError(friendlyError(e));
        setPhase('error');
      }
    } finally {
      if (ridRef.current) void linkingApi.close(ridRef.current).catch(() => undefined);
      ridRef.current = null;
      runningRef.current = false;
    }
  }, [me.id, confirmSas, onProgress]);

  const startCompanion = useCallback(
    async (scanned: string) => {
      if (runningRef.current) return;
      runningRef.current = true;
      try {
        const qr = parseQr(scanned);
        setPhase('connecting');
        const LinkHandshake = await loadLinking();
        const handshake = new LinkHandshake();
        const { received } = await runCompanion({
          relay: relayFor(qr.rid, 'c'),
          handshake,
          accountId: me.id,
          qr,
          sink: historySink(),
          confirmSas,
          onProgress,
        });
        await refreshAfterTransfer();
        setCount(received);
        setPhase('done');
      } catch (e) {
        if (e instanceof LinkAbortedError) setPhase('aborted');
        else {
          setError(friendlyError(e));
          setPhase('error');
        }
      } finally {
        runningRef.current = false;
      }
    },
    [me.id, confirmSas, onProgress],
  );

  return (
    <Modal title="Link a device" onClose={onClose} width={440}>
      <div className="bz-link-body">{renderPhase()}</div>
    </Modal>
  );

  function renderPhase() {
    switch (phase) {
      case 'choose':
        return (
          <>
            <p className="bz-link-lede">
              Bring your Discussions history to another device, end to end. Nothing leaves your
              devices unencrypted — not even to our servers.
            </p>
            <Button variant="primary" block onClick={startPrimary}>
              This device has my history — show a code
            </Button>
            <Button variant="outline" block onClick={goScanning}>
              This is the new device — scan a code
            </Button>
          </>
        );

      case 'showing':
        return (
          <div className="bz-link-center">
            <QrShow text={qrText} />
            <p className="bz-link-hint">Scan this with your other device to bring your history there.</p>
            <details className="bz-link-details">
              <summary>Can’t scan? Show the code</summary>
              <textarea className="bz-link-paste-input" readOnly value={qrText} rows={3} onFocus={selectAll} />
            </details>
            <div className="bz-link-waiting">
              <BrizinaLoader />
              <span>Waiting for the other device…</span>
            </div>
          </div>
        );

      case 'scanning':
        return <QrScan onResult={startCompanion} />;

      case 'connecting':
        return (
          <div className="bz-link-waiting bz-link-center">
            <BrizinaLoader />
            <span>Connecting to your other device…</span>
          </div>
        );

      case 'sas':
        return (
          <div className="bz-link-center">
            <p className="bz-link-lede">Check that this same number shows on both devices, then confirm.</p>
            <div className="bz-link-sas" aria-label={`Safety number ${sas}`}>
              {sas.slice(0, 3)} {sas.slice(3)}
            </div>
            <Button variant="primary" block onClick={onSasMatch}>
              The numbers match
            </Button>
            <Button variant="danger" block onClick={onSasNoMatch}>
              They don’t match
            </Button>
          </div>
        );

      case 'transfer': {
        const pct = progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : null;
        return (
          <div className="bz-link-waiting bz-link-center">
            <div className={`bz-chat-ring${pct === null ? ' bz-chat-ring-spin' : ''}`}>
              {pct !== null && <span className="bz-chat-ring-pct">{pct}%</span>}
            </div>
            <span>Transferring history… {progress.done > 0 && `${progress.done} messages`}</span>
          </div>
        );
      }

      case 'done':
        return (
          <div className="bz-link-center">
            <p className="bz-link-done">Done — {count} {count === 1 ? 'message' : 'messages'} synced.</p>
            <Button variant="primary" block onClick={onClose}>
              Close
            </Button>
          </div>
        );

      case 'aborted':
        return (
          <div className="bz-link-center">
            <p className="bz-link-lede">
              Linking was stopped because the safety numbers didn’t match. That can mean a network
              attacker — start over on a trusted connection.
            </p>
            <Button variant="outline" block onClick={goChoose}>
              Start over
            </Button>
          </div>
        );

      case 'error':
        return (
          <div className="bz-link-center">
            <p className="bz-link-error">{error}</p>
            <Button variant="outline" block onClick={goChoose}>
              Try again
            </Button>
          </div>
        );
    }
  }
}
